// Live, in-progress measurement feedback while a shape is still being
// sketched — a cursor-following help tooltip ("Double click to finish your
// zone"), per-edge length labels along a Zone's sides as vertices are
// added, and a running total (area for Zone/Circle, length for Line) near
// the shape. Standard OpenLayers pattern (see the OL "Measure" example);
// extended here with the per-segment labels the plan's reference asked for.
// All of this is imperative OL code by necessity (Overlay positions must
// track the sketch geometry on every pointer move) — kept out of
// MeasureToolbar.tsx's React tree on purpose, same reasoning as any other
// OL interaction/overlay wiring in this codebase.
import Overlay from 'ol/Overlay';
import type Map from 'ol/Map';
import type Draw from 'ol/interaction/Draw';
import type { DrawEvent } from 'ol/interaction/Draw';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import CircleGeom from 'ol/geom/Circle';
import { getArea, getLength } from 'ol/sphere';
import { formatArea, formatLength } from '../../map/measurementFormat';
import type { MeasurementType } from '../../map/measurement';

const START_MESSAGE: Record<MeasurementType, string> = {
  point: 'Click the map to drop a point.',
  line: 'Click to start drawing a line.',
  zone: 'Click to start drawing a zone.',
  circle: 'Click and drag to draw a circle.',
};

const FINISH_MESSAGE: Partial<Record<MeasurementType, string>> = {
  line: 'Click to add a point, double-click to finish your line.',
  zone: 'Click to add a point, double-click to finish your zone.',
};

function makeOverlay(className: string, options: ConstructorParameters<typeof Overlay>[0] = {}): { el: HTMLDivElement; overlay: Overlay } {
  const el = document.createElement('div');
  el.className = className;
  const overlay = new Overlay({ element: el, stopEvent: false, ...options });
  return { el, overlay };
}

/**
 * Attaches live-measurement overlays to a Draw interaction. Returns a
 * cleanup function that removes every overlay and listener it added.
 */
export function attachDrawOverlays(map: Map, draw: Draw, toolId: MeasurementType): () => void {
  const help = makeOverlay('measure-overlay measure-overlay-help', {
    offset: [16, 0],
    positioning: 'center-left',
  });
  help.el.textContent = START_MESSAGE[toolId];
  map.addOverlay(help.overlay);

  let total: { el: HTMLDivElement; overlay: Overlay } | null = null;
  let segments: Array<{ el: HTMLDivElement; overlay: Overlay }> = [];
  let geomChangeKey: EventsKey | null = null;

  function clearSegments() {
    segments.forEach((s) => map.removeOverlay(s.overlay));
    segments = [];
  }

  function ensureTotal() {
    if (total) return total;
    total = makeOverlay('measure-overlay measure-overlay-total', {
      positioning: 'bottom-center',
      offset: [0, -10],
    });
    map.addOverlay(total.overlay);
    return total;
  }

  function update(geom: Geometry) {
    const projection = map.getView().getProjection();

    if (geom instanceof Polygon) {
      const ring = geom.getCoordinates()[0] ?? [];
      clearSegments();
      for (let i = 0; i < ring.length - 1; i++) {
        const a = ring[i];
        const b = ring[i + 1];
        const length = getLength(new LineString([a, b]), { projection });
        if (length < 0.5) continue; // the auto-closing segment before a 2nd real vertex exists
        const seg = makeOverlay('measure-overlay measure-overlay-segment', {
          positioning: 'center-center',
          position: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
        });
        seg.el.textContent = formatLength(length);
        map.addOverlay(seg.overlay);
        segments.push(seg);
      }
      const t = ensureTotal();
      const area = getArea(geom, { projection });
      t.el.textContent = area > 0 ? formatArea(area) : '';
      t.overlay.setPosition(geom.getInteriorPoint().getCoordinates());
      return;
    }

    if (geom instanceof LineString) {
      const t = ensureTotal();
      t.el.textContent = formatLength(getLength(geom, { projection }));
      t.overlay.setPosition(geom.getLastCoordinate());
      return;
    }

    if (geom instanceof CircleGeom) {
      const t = ensureTotal();
      const center = geom.getCenter();
      const edge = [center[0] + geom.getRadius(), center[1]];
      const radius = getLength(new LineString([center, edge]), { projection });
      const area = getArea(geom, { projection });
      t.el.textContent = `${formatLength(radius)} radius · ${formatArea(area)}`;
      t.overlay.setPosition(edge);
    }
  }

  function cleanupSketch() {
    clearSegments();
    if (total) {
      map.removeOverlay(total.overlay);
      total = null;
    }
    if (geomChangeKey) {
      unByKey(geomChangeKey);
      geomChangeKey = null;
    }
  }

  const startKey = draw.on('drawstart', (evt: DrawEvent) => {
    const msg = FINISH_MESSAGE[toolId];
    if (msg) help.el.textContent = msg;
    const geom = evt.feature.getGeometry();
    if (!geom) return;
    update(geom);
    geomChangeKey = geom.on('change', (e) => update(e.target as Geometry));
  });

  const endKey = draw.on('drawend', () => {
    cleanupSketch();
    help.el.textContent = START_MESSAGE[toolId];
  });

  // Escape-cancel (MeasureToolbar.tsx calls draw.abortDrawing()) fires
  // 'drawabort' instead of 'drawend' — same live-label cleanup, just
  // without ever calling addMeasurement for the (never finished) shape.
  const abortKey = draw.on('drawabort', () => {
    cleanupSketch();
    help.el.textContent = START_MESSAGE[toolId];
  });

  const moveKey = map.on('pointermove', (evt) => {
    help.overlay.setPosition(evt.coordinate);
  });

  return () => {
    unByKey(startKey);
    unByKey(endKey);
    unByKey(abortKey);
    unByKey(moveKey);
    cleanupSketch();
    map.removeOverlay(help.overlay);
  };
}
